'use client';

/**
 * Synchronizes tracking parameters with the browser URL without triggering a page reload.
 * Deduplicates keys, normalizes casing variations, and updates browser history in place.
 *
 * @param options - Function parameters.
 * @param options.searchParams - Existing URL search parameters.
 * @param options.mergedParams - Canonical tracking parameter keys and their target values.
 * @param options.pathname - Current URL path (e.g., `window.location.pathname`).
 */
export function persistTrackingParamsInUrl({
  searchParams,
  mergedParams,
  pathname,
}: {
  searchParams: URLSearchParams;
  mergedParams: Record<string, string | undefined>;
  pathname: string;
}) {
  const currentParams = new URLSearchParams(searchParams.toString());
  let urlNeedsUpdate = false;

  Object.entries(mergedParams).forEach(([key, value]) => {
    const matchingKeys = Array.from(currentParams.keys()).filter(
      (k) => k.toLowerCase() === key.toLowerCase(),
    );

    const isCasingIncorrect = matchingKeys.some((k) => k !== key);
    const isValueDifferent = currentParams.get(key) !== value;
    const hasDuplicates = matchingKeys.length > 1;

    if (isCasingIncorrect || isValueDifferent || hasDuplicates) {
      // Delete all existing case variations (e.g., delete 'gclID')
      matchingKeys.forEach((key) => currentParams.delete(key));
      currentParams.set(key, value ?? '');
      urlNeedsUpdate = true;
    }
  });

  if (urlNeedsUpdate) {
    const newUrl = `${pathname}?${currentParams.toString()}`;
    window.history.replaceState(null, '', newUrl);
  }
}
